import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Loader2, UserPlus, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { userService } from '@/services/userService';
import { locationService } from '@/services/locationService';
import type { ApplicationRole } from '@/types/user';
import { onboardingService } from '../services/onboardingService';
import type {
  CreateUserFromCaseResponse,
  UserCreationPrefill,
} from '../services/onboardingService';

/**
 * Creates a MIS / FusionAuth user from an onboarded candidate via
 * `POST /console/user_onboarding/create/user/:case_id`.
 *
 * Spec: docs/onboarding/API/User-Onboarding.md §4.1
 *
 * The body only carries `roles`, `permanent_address`, and (optional)
 * `district`. Identity fields (name, email, employee id, gender,
 * joining_date, etc.) are loaded server-side from the case and shown
 * here read-only so the reviewer can verify before submitting. On
 * success the server may return a one-time `password` for the new
 * user — surfaced via a copy-to-clipboard panel.
 */

interface Props {
  caseId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const formatList = (
  roles: Array<string | ApplicationRole> | undefined,
): { value: string; label: string }[] => {
  if (!roles) return [];
  return roles
    .map((r) => {
      if (typeof r === 'string') return { value: r, label: r };
      if (r && typeof r === 'object' && typeof (r as any).id === 'string') {
        return { value: (r as any).id as string, label: ((r as any).name as string) || (r as any).id };
      }
      return null;
    })
    .filter((x): x is { value: string; label: string } => !!x);
};

export const CreateUserFromCaseModal = ({ caseId, isOpen, onClose, onSuccess }: Props) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  // Server-side prefill — identity fields + suggested permanent address.
  const prefillQuery = useQuery({
    queryKey: ['case-user-create-prefill', caseId],
    queryFn: () => onboardingService.getUserCreationPrefill(caseId),
    enabled: isOpen,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const rolesQuery = useQuery({
    queryKey: ['application-roles'],
    queryFn: () => userService.getApplicationRoles(),
    enabled: isOpen,
  });

  const districtsQuery = useQuery({
    queryKey: ['districts'],
    queryFn: () => locationService.getDistrictOptions(),
    enabled: isOpen,
  });

  // Editable user inputs — initialised from prefill when it lands.
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [permanentAddress, setPermanentAddress] = useState('');
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [createdResponse, setCreatedResponse] = useState<CreateUserFromCaseResponse | null>(null);

  // Apply the suggested permanent address once the prefill resolves.
  // We deliberately don't re-apply on refetch so user edits aren't clobbered.
  const hasAppliedPrefillRef = useMemo(() => ({ current: false }), []);
  useEffect(() => {
    if (!isOpen) {
      hasAppliedPrefillRef.current = false;
      return;
    }
    if (hasAppliedPrefillRef.current) return;
    const suggested = prefillQuery.data?.permanent_address;
    if (typeof suggested === 'string' && suggested.length > 0) {
      setPermanentAddress(suggested);
      hasAppliedPrefillRef.current = true;
    }
  }, [isOpen, prefillQuery.data, hasAppliedPrefillRef]);

  // Reset transient state every time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setSelectedRoles([]);
      setSelectedDistricts([]);
      setFormError(null);
      setCreatedResponse(null);
    }
  }, [isOpen]);

  const availableRoles = useMemo(
    () => formatList(rolesQuery.data?.roles),
    [rolesQuery.data?.roles],
  );

  const createMutation = useMutation({
    mutationFn: () =>
      onboardingService.createUserFromCase(caseId, {
        roles: selectedRoles,
        permanent_address: permanentAddress.trim(),
        // `""` = empty set; `undefined` gets a fallback district assigned.
        district: selectedDistricts.length > 0 ? selectedDistricts.join(',') : '',
      }),
    onSuccess: (response) => {
      setCreatedResponse(response);
      const password = response?.data?.password;
      // No password in the response → straight success (typically
      // reactivation path). Otherwise we keep the modal open so the
      // reviewer can copy the one-time password before closing.
      if (!password) {
        showSuccess(
          'User Created',
          response?.message || 'Account created from candidate data.',
        );
        queryClient.invalidateQueries({ queryKey: ['onboarding-case', caseId] });
        queryClient.invalidateQueries({ queryKey: ['workflow-state', caseId] });
        queryClient.invalidateQueries({ queryKey: ['onboarding-cases'] });
        onSuccess();
      }
    },
    onError: (err: any) => {
      setFormError(err?.message || 'Could not create user.');
      showError('Create User Failed', err?.message || 'Could not create user.');
    },
  });

  if (!isOpen) return null;

  const prefill = prefillQuery.data;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (selectedRoles.length === 0) {
      setFormError('Pick at least one role for the new user.');
      return;
    }
    if (!permanentAddress.trim()) {
      setFormError('Permanent address is required.');
      return;
    }
    createMutation.mutate();
  };

  const handleClose = () => {
    if (createMutation.isPending) return;
    if (createdResponse) {
      // We held the modal open to surface the password — close = success.
      queryClient.invalidateQueries({ queryKey: ['onboarding-case', caseId] });
      queryClient.invalidateQueries({ queryKey: ['workflow-state', caseId] });
      queryClient.invalidateQueries({ queryKey: ['onboarding-cases'] });
      onSuccess();
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-rgf-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-rgf-gray-900 flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-rgf-primary-700" />
            Create user from candidate
          </h2>
          <button
            type="button"
            aria-label="Close"
            className="p-1 text-rgf-gray-400 hover:text-rgf-gray-700"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-5">
          {prefillQuery.isLoading ? (
            <p className="text-sm text-rgf-gray-500">Loading candidate details…</p>
          ) : prefillQuery.error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {(prefillQuery.error as Error)?.message ||
                'Could not load candidate details. The candidate may not yet be eligible.'}
            </div>
          ) : prefill ? (
            <>
              <ReadOnlyIdentity prefill={prefill} />

              {createdResponse ? (
                <ResultPanel response={createdResponse} onDone={handleClose} />
              ) : (
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <RolesPicker
                    roles={availableRoles}
                    selected={selectedRoles}
                    onChange={setSelectedRoles}
                    loading={rolesQuery.isLoading}
                  />

                  <div>
                    <label className="text-sm font-medium text-rgf-gray-700">
                      Permanent Address <span className="text-red-600">*</span>
                    </label>
                    <textarea
                      className="form-input mt-1 w-full"
                      rows={2}
                      value={permanentAddress}
                      onChange={(e) => setPermanentAddress(e.target.value)}
                    />
                    <p className="text-xs text-rgf-gray-500 mt-1">
                      Suggested from the candidate's village. Edit as needed.
                    </p>
                  </div>

                  <DistrictsPicker
                    options={districtsQuery.data ?? []}
                    selected={selectedDistricts}
                    onChange={setSelectedDistricts}
                    loading={districtsQuery.isLoading}
                  />

                  {formError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {formError}
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-rgf-gray-100">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      disabled={createMutation.isPending}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={createMutation.isPending}>
                      {createMutation.isPending ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Creating…
                        </span>
                      ) : (
                        'Create User'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ── sub-components ────────────────────────────────────────────────────────

const ReadOnlyIdentity = ({ prefill }: { prefill: UserCreationPrefill }) => {
  const fullName = [prefill.first_name, prefill.middle_name, prefill.last_name]
    .filter((s) => typeof s === 'string' && s.length > 0)
    .join(' ');
  return (
    <section className="rounded-lg border border-rgf-gray-200 bg-rgf-gray-50 p-3 sm:p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-rgf-gray-500 mb-2">
        Identity (from candidate, read-only)
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <IdentityRow label="Name" value={fullName || '—'} />
        <IdentityRow label="Employee ID" value={prefill.employee_id || '—'} />
        <IdentityRow label="Email" value={prefill.email || '—'} />
        <IdentityRow label="Phone" value={prefill.phone_number || '—'} />
        <IdentityRow label="Gender" value={prefill.gender || '—'} />
        <IdentityRow label="Joining Date" value={prefill.joining_date || '—'} />
      </div>
      {prefill.db_id && (
        <p className="mt-3 text-xs text-amber-700">
          A user record is already linked to this candidate (db_id: {prefill.db_id}). Server may
          reject the create or attempt a reactivation depending on status.
        </p>
      )}
    </section>
  );
};

const IdentityRow = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-rgf-gray-500">{label}</p>
    <p className="text-sm text-rgf-gray-900 break-words">{value}</p>
  </div>
);

interface RolesPickerProps {
  roles: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  loading: boolean;
}

const RolesPicker = ({ roles, selected, onChange, loading }: RolesPickerProps) => {
  const toggle = (value: string) => {
    if (selected.includes(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  };
  return (
    <div>
      <label className="text-sm font-medium text-rgf-gray-700">
        Roles <span className="text-red-600">*</span>
      </label>
      {loading ? (
        <p className="text-sm text-rgf-gray-500 mt-1">Loading roles…</p>
      ) : roles.length === 0 ? (
        <p className="text-sm text-rgf-gray-500 mt-1">No application roles available.</p>
      ) : (
        <div className="mt-2 max-h-40 overflow-y-auto rounded border border-rgf-gray-200 p-2 space-y-1">
          {roles.map((r) => (
            <label key={r.value} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(r.value)}
                onChange={() => toggle(r.value)}
              />
              <span className="text-rgf-gray-800">{r.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

interface DistrictsPickerProps {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  loading: boolean;
}

const DistrictsPicker = ({ options, selected, onChange, loading }: DistrictsPickerProps) => {
  const toggle = (value: string) => {
    if (selected.includes(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  };
  return (
    <div>
      <label className="text-sm font-medium text-rgf-gray-700">District (optional)</label>
      {loading ? (
        <p className="text-sm text-rgf-gray-500 mt-1">Loading districts…</p>
      ) : options.length === 0 ? (
        <p className="text-sm text-rgf-gray-500 mt-1">No districts available.</p>
      ) : (
        <div className="mt-2 max-h-32 overflow-y-auto rounded border border-rgf-gray-200 p-2 grid grid-cols-2 gap-1">
          {options.map((d) => (
            <label key={d.value} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(d.value)}
                onChange={() => toggle(d.value)}
              />
              <span className="text-rgf-gray-800">{d.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const ResultPanel = ({
  response,
  onDone,
}: {
  response: CreateUserFromCaseResponse;
  onDone: () => void;
}) => {
  const password = response?.data?.password;
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };
  return (
    <section className="space-y-4">
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
        {response?.message || 'User created successfully.'}
      </div>
      {password && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <KeyRound className="h-4 w-4" />
            One-time password
          </div>
          <p className="text-xs text-amber-800 mt-1">
            Share this with the new user securely. It will not be shown again.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 rounded bg-white border border-amber-200 px-2 py-1 font-mono text-sm break-all">
              {password}
            </code>
            <Button type="button" variant="secondary" size="sm" icon={<Copy className="h-3.5 w-3.5" />} onClick={handleCopy}>
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      )}
      <div className="flex justify-end">
        <Button type="button" onClick={onDone}>
          Done
        </Button>
      </div>
    </section>
  );
};
